import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import AdminModal from '../AdminModal.js';
import { getAvatarColor, getInitials } from './teamAvatar.js';

const TeamDetailModal = ({ isOpen, onClose, team, users, roles, onUpdate, loading }) => {
  const [teamName, setTeamName] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [addedUserIds, setAddedUserIds] = useState([]);
  const [removedUserIds, setRemovedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (team) {
      setTeamName(team.teamName || '');
      setTeamMembers(team.userIds || []);
      setAddedUserIds([]);
      setRemovedUserIds([]);
      setUserSearch('');
    }
  }, [team]);

  const handleAddUser = (userId) => {
    if (!teamMembers.includes(userId)) {
      setTeamMembers((prev) => [...prev, userId]);
      setAddedUserIds((prev) => [...prev, userId]);
      setRemovedUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleRemoveUser = (userId) => {
    setTeamMembers((prev) => prev.filter((id) => id !== userId));
    setRemovedUserIds((prev) => [...prev, userId]);
    setAddedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleUpdate = () => {
    const updates = {
      teamId: team.teamId,
      teamName: teamName !== team.teamName ? teamName : undefined,
      addedUserIds: addedUserIds.length > 0 ? addedUserIds : undefined,
      removedUserIds: removedUserIds.length > 0 ? removedUserIds : undefined,
    };
    onUpdate(updates);
  };

  const hasChanges = teamName !== team?.teamName || addedUserIds.length > 0 || removedUserIds.length > 0;
  const currentMembers = users.filter((user) => teamMembers.includes(user.userId));
  const searchLower = userSearch.toLowerCase();
  const filteredUsers = userSearch.trim()
    ? users.filter((user) =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      )
    : users;

  const getRoleLabel = (user) => {
    if (!user.role) return null;
    return roles?.[user.role]?.title || user.role;
  };

  return (
    <AdminModal
      isOpen={isOpen && !!team}
      onClose={onClose}
      containerClassName="team-detail-modal"
      headerClassName="team-detail-modal-header"
      wrapContent={false}
      title={(
        <div className="team-modal-title-group">
          <h2>{team?.teamName}</h2>
          <p className="team-modal-subtitle">Manage who belongs to this team</p>
        </div>
      )}
      actions={(
        <>
          <button className="admin-modal-cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="admin-submit-button"
            onClick={handleUpdate}
            disabled={loading || !hasChanges}
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save changes'}
          </button>
        </>
      )}
    >
      <div className="team-name-edit-bar">
        <label htmlFor="teamNameEdit" className="team-name-edit-label">Team name</label>
        <input
          type="text"
          id="teamNameEdit"
          className="team-name-edit-input"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          placeholder="Team name"
        />
      </div>

      <div className="team-dual-panel">
        <div className="team-panel team-panel-left">
          <div className="team-panel-header">
            <span className="team-panel-label">All users</span>
          </div>
          <div className="team-panel-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="team-search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="team-search-input"
            />
          </div>
          <div className="team-panel-list">
            {filteredUsers.length === 0 ? (
              <p className="team-panel-empty">No users match your search</p>
            ) : (
              filteredUsers.map((user) => {
                const isMember = teamMembers.includes(user.userId);
                return (
                  <div key={user.userId} className="team-user-row">
                    <div
                      className="team-user-avatar"
                      style={{ backgroundColor: getAvatarColor(user.name) }}
                    >
                      {getInitials(user.name)}
                    </div>
                    <div className="team-user-info">
                      <span className="team-user-name">{user.name}</span>
                      {getRoleLabel(user) && (
                        <span className="team-user-role">{getRoleLabel(user)}</span>
                      )}
                    </div>
                    {isMember ? (
                      <span className="team-in-team-pill">In team</span>
                    ) : (
                      <button
                        className="team-add-button"
                        onClick={() => handleAddUser(user.userId)}
                        disabled={loading}
                      >
                        Add →
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="team-panel team-panel-right">
          <div className="team-panel-header">
            <span className="team-panel-label">Team members ({currentMembers.length})</span>
            <span className="team-panel-subtext">Click to remove</span>
          </div>
          <div className="team-panel-list">
            {currentMembers.length === 0 ? (
              <div className="team-empty-state">Add users from the left panel</div>
            ) : (
              currentMembers.map((user) => (
                <div key={user.userId} className="team-user-row">
                  <div
                    className="team-user-avatar"
                    style={{ backgroundColor: getAvatarColor(user.name) }}
                  >
                    {getInitials(user.name)}
                  </div>
                  <div className="team-user-info">
                    <span className="team-user-name">{user.name}</span>
                    {getRoleLabel(user) && (
                      <span className="team-user-role">{getRoleLabel(user)}</span>
                    )}
                  </div>
                  <button
                    className="team-remove-button"
                    onClick={() => handleRemoveUser(user.userId)}
                    disabled={loading}
                    title="Remove from team"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminModal>
  );
};

export default TeamDetailModal;
